import { Injectable, computed, signal } from '@angular/core';

import { OperatorSocketService } from '../socket/operator-socket.service';

@Injectable({ providedIn: 'root' })
export class CaseUnreadService {
  private readonly counts = signal<Record<string, number>>({});
  private activeConversationId: string | null = null;

  /** Total unread across all conversations. */
  readonly total = computed(() =>
    Object.values(this.counts()).reduce((sum, n) => sum + n, 0),
  );

  constructor(private readonly socket: OperatorSocketService) {
    this.socket.messageCreated$.subscribe((msg) => {
      if (msg.sender_type === 'OPERATOR') return;
      if (msg.conversation_id === this.activeConversationId) return;
      this.counts.update((c) => ({
        ...c,
        [msg.conversation_id]: (c[msg.conversation_id] ?? 0) + 1,
      }));
    });
  }

  /**
   * Call when the operator opens a conversation.
   * Clears that conversation's count and marks it as active.
   * Pass null when leaving My Cases entirely.
   */
  setActive(conversationId: string | null): void {
    this.activeConversationId = conversationId;
    if (conversationId) this.clearConversation(conversationId);
  }

  getCount(conversationId: string): number {
    return this.counts()[conversationId] ?? 0;
  }

  /**
   * Drop a conversation's unread state entirely. Call when a case is closed or
   * returned to AI so the in-memory map doesn't grow unbounded over a long
   * operator session (M2).
   */
  forget(conversationId: string): void {
    this.clearConversation(conversationId);
  }

  private clearConversation(conversationId: string): void {
    this.counts.update((c) => {
      if (!c[conversationId]) return c;
      const next = { ...c };
      delete next[conversationId];
      return next;
    });
  }
}